import catalog from './model-catalog.json' with {type:'json'};
import {listRimTemplates,applyRimTemplate} from './rim-templates.mjs';

import {requestEditor} from '../studio/editor-rpc.mjs';
export {requestEditor};

export async function executeAgentCommand(window,{command,args={}}) {
  const bridge=window.MockupStudio;
  if(!bridge?.ready) throw new Error('Studio viewport is not ready');
  if (['select_model','set_controls','import_image','import_media','edit_timeline'].includes(command)) await bridge.pauseTimeline?.();
  switch(command) {
    case 'get_state': return {...bridge.getState(),renderer:bridge.getRendererState()};
    case 'list_controls': return bridge.getControls();
    case 'list_models': return catalog.filter(model=>model.available).map(({id,label,kind})=>({id,label,kind}));
    case 'select_model': {
      const model=catalog.find(model=>model.id===args.modelId&&model.available);
      if(!model) throw new Error('Model is not installed locally');
      return bridge.selectModel(model.kind==='flat'?'':model.id);
    }
    case 'set_controls': return bridge.commit(args.patch);
    case 'list_templates': return listRimTemplates();
    case 'apply_template': return applyRimTemplate(bridge,args.templateId);
    case 'import_image': case 'export_image': case 'get_timeline': case 'set_playhead': case 'edit_timeline':
    case 'get_project': case 'open_project': case 'save_project': case 'import_media':
    case 'export_video': case 'get_export_status': case 'read_export':
      return requestEditor(window,command,args);
    default: throw new Error('Unsupported studio command');
  }
}

export function startAgentConnection({window,execute=command=>executeAgentCommand(window,command)}={}) {
  const key='mockstudio-agent-session-v1';
  let disposed=false, enabled=true, controller=null, enrollment=null, trigger, status='Connecting';
  try { enabled=window.localStorage.getItem('mockstudio-agents-paused')!=='1'; } catch {}
  try { enrollment=JSON.parse(window.sessionStorage.getItem(key)); } catch {}
  const remember=()=>{try{enrollment ? window.sessionStorage.setItem(key,JSON.stringify(enrollment)):window.sessionStorage.removeItem(key);}catch{}};
  const render=()=>{
    if(!trigger?.isConnected) {
      const anchor=window.document.querySelector('[data-local-models]');
      if(!anchor) return;
      trigger=window.document.createElement('button');
      trigger.type='button';trigger.dataset.studioAgents='';trigger.textContent='Agents';
      trigger.addEventListener('click',()=>void toggle());
      anchor.after(trigger);
    }
    trigger.setAttribute('aria-pressed',String(enabled));
    trigger.setAttribute('aria-label',enabled ? 'Pause agent control' : 'Enable agent control');
    trigger.title=enabled ? `Agent control: ${status}. Click to pause.` : 'Agent control paused. Click to enable.';
    trigger.dataset.agentStatus=status.toLowerCase();
  };
  const observer=new window.MutationObserver(render);
  observer.observe(window.document.documentElement,{childList:true,subtree:true}); render();
  async function post(path,body,signal) {
    const response=await window.fetch('/api/agent/'+path,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body),signal});
    const data=await response.json();
    if(!response.ok){const error=new Error(data.error||`Agent connection ${response.status}`);error.status=response.status;throw error;}
    return data;
  }
  async function toggle() {
    enabled=!enabled;
    try{window.localStorage.setItem('mockstudio-agents-paused',enabled?'0':'1');}catch{}
    controller?.abort();
    if(!enabled&&enrollment){try{await post('disconnect',enrollment);}catch{} enrollment=null;remember();}
    status=enabled?'Connecting':'Paused'; render();
  }
  const wait=(ms=1200)=>new Promise(resolve=>window.setTimeout(resolve,ms));
  // The tab reports which saved project it shows, so agents can find it.
  const context=()=>{
    try { const projectId=new URLSearchParams(window.location.search).get('project'); return projectId?{projectId}:{}; }
    catch { return {}; }
  };
  async function run() {
    while(!disposed) {
      if(!enabled||!window.MockupStudio?.ready){await wait();continue;}
      controller=new window.AbortController();
      try {
        if(!enrollment){enrollment=await post('connect',{},controller.signal);remember();}
        status='Ready';render();
        const {command}=await post('poll',{...enrollment,context:context()},controller.signal);
        if(!command) continue;
        if(!enabled) continue;
        status='Working';render();
        let result,error;
        try{result=await execute(command);}catch(failure){error=failure.message||String(failure);}
        await post('result',{...enrollment,id:command.id,...(error?{error}:{result})},controller.signal);
      } catch(error) {
        if(disposed) break;
        if(error.name==='AbortError') continue;
        if([401,403,404,410].includes(error.status)){enrollment=null;remember();}
        // Enrollment needs a signed-in account; retry slowly until one exists.
        const signedOut=error.status===401&&/sign in/i.test(error.message);
        status=signedOut?'Sign in required':'Unavailable';render(); await wait(signedOut?15000:1200);
      }
    }
  }
  void run();
  return {dispose(){disposed=true;controller?.abort();observer.disconnect();trigger?.remove();}};
}
